import React, { useEffect, useRef } from 'react';
import { Animated, Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { MaterialCommunityIcons as Icon } from '@expo/vector-icons';
import { REDESIGN_COLORS } from '../../styles/colors';
import { useTier } from '../../context/TierContext';

// D-026 copy matrix. Keeps the voice low-pressure and value-forward (see
// upgradeCopy.js SOURCE_COPY for the UpgradeModal body on tap). Each trigger
// has a dedicated PostHog source so we can measure lift per trip-wire.
const BRANCH_COPY = {
  listings_viewed_3: {
    headline: 'Enjoying the hunt?',
    body: 'Keep full breakdowns on every listing past your trial with Pro.',
    source: 'trial_aha_listings_viewed_3',
  },
  dealer_click_1: {
    headline: 'Going to see one?',
    body: 'Keep your scores and analysis live past your trial with Pro.',
    source: 'trial_aha_dealer_click_1',
  },
  saves_2: {
    headline: 'Comparing your saves?',
    body: 'Pro keeps your saved list and score breakdowns after your trial.',
    source: 'trial_aha_saves_2',
  },
  neg_2: {
    headline: 'Building momentum?',
    body: 'Keep your negotiations live past your trial with Pro.',
    source: 'trial_aha_neg_2',
  },
};

const TrialAhaToast = () => {
  const { ahaToast, dismissAhaToast, triggerUpgradeModal } = useTier();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const slideAnim = useRef(new Animated.Value(-40)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!ahaToast) return;
    Animated.parallel([
      Animated.timing(slideAnim, { toValue: 0, duration: 320, useNativeDriver: true }),
      Animated.timing(fadeAnim, { toValue: 1, duration: 320, useNativeDriver: true }),
    ]).start();
    // No auto-dismiss — user dismisses via the close button or the CTA.
  }, [ahaToast, slideAnim, fadeAnim]);

  if (!ahaToast) return null;
  const copy = BRANCH_COPY[ahaToast.trigger];
  if (!copy) return null;

  const handleUpgrade = () => {
    dismissAhaToast();
    triggerUpgradeModal({ source: copy.source });
  };

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[
        styles.wrapper,
        isMobile ? styles.wrapperMobile : styles.wrapperDesktop,
        { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
      ]}
    >
      <View style={styles.card}>
        <View style={styles.iconWrap}>
          <Icon name="lightbulb-on-outline" size={18} color={REDESIGN_COLORS.ctaBlue} />
        </View>
        <View style={styles.content}>
          <Text style={styles.headline}>{copy.headline}</Text>
          <Text style={styles.body}>{copy.body}</Text>
          <Pressable onPress={handleUpgrade} style={styles.ctaBtn} accessibilityRole="button" accessibilityLabel="Upgrade to Pro">
            <Text style={styles.ctaText}>Upgrade to Pro</Text>
            <Icon name="arrow-right" size={14} color={REDESIGN_COLORS.textPrimary} />
          </Pressable>
        </View>
        <Pressable onPress={dismissAhaToast} hitSlop={8} accessibilityRole="button" accessibilityLabel="Dismiss" style={styles.closeBtn}>
          <Icon name="close" size={16} color={REDESIGN_COLORS.textSecondary} />
        </Pressable>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    zIndex: 9998,
  },
  wrapperMobile: {
    top: 12, left: 12, right: 12,
  },
  wrapperDesktop: {
    top: 20,
    left: '50%',
    marginLeft: -180,
    width: 360,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(83,138,249,0.35)',
    backgroundColor: '#0E264D',
    ...(Platform.OS === 'web' && {
      backgroundImage:
        'radial-gradient(240px 120px at 100% 0%, rgba(83,138,249,0.22), transparent 60%), linear-gradient(180deg, rgba(20,76,184,0.28) 0%, rgba(14,38,77,0.9) 100%)',
      boxShadow: '0 12px 28px rgba(5,86,247,0.2)',
    }),
  },
  iconWrap: {
    width: 32, height: 32, borderRadius: 16,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(5,86,247,0.18)',
    borderWidth: 1, borderColor: 'rgba(83,138,249,0.4)',
  },
  content: { flex: 1 },
  headline: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
    marginBottom: 4,
  },
  body: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    lineHeight: 17,
    color: 'rgba(255,255,255,0.75)',
  },
  ctaBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    marginTop: 10,
  },
  ctaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    color: REDESIGN_COLORS.textPrimary,
  },
  closeBtn: {
    padding: 2,
  },
});

export default TrialAhaToast;
